// Chart geometry for the `chart` widget kind (arc 101 slice 09, task #81). Pure +
// dependency-free: reduces a widget's result rows into a normalized model the
// <ChartWidget/> draws as CSS columns. No charting runtime is pulled into the SPA
// bundle — the shapes here (labels × series × normalized heights) are all a
// bar/column view needs, and richer typed charts can layer on later.

import type { ChartWidget, PageRow } from '@developerz/domain';

/** One plotted value under an x-axis label. */
export type ChartPoint = { readonly label: string; readonly value: number };

/** One series across every x label, in row order. */
export type ChartSeriesData = {
  readonly key: string;
  readonly label: string;
  readonly points: readonly ChartPoint[];
};

/** The normalized chart — series aligned to a shared label axis + `max`, the
 * largest ABSOLUTE value, used to scale bar heights (`>= 0`, so a division guard
 * is unnecessary downstream). Bars are sized by magnitude so a negative or
 * mixed-sign series still renders instead of collapsing to an empty plot; the
 * exact signed value stays in each point, the bar tooltip, and the sr-only table.
 * A true below-zero baseline is a later, richer-chart concern. */
export type ChartModel = {
  readonly labels: readonly string[];
  readonly series: readonly ChartSeriesData[];
  readonly max: number;
  readonly hasData: boolean;
};

function toNumber(value: unknown): number {
  const n = typeof value === 'number' ? value : typeof value === 'string' ? Number(value) : NaN;
  return Number.isFinite(n) ? n : 0;
}

/** Reduce result rows into the chart model for one widget spec. */
export function chartModel(spec: ChartWidget, rows: readonly PageRow[]): ChartModel {
  const labels = rows.map((r) => String(r[spec.x] ?? ''));
  const series = spec.series.map((s) => ({
    key: s.key,
    label: s.label,
    points: rows.map((r, i) => ({ label: labels[i] ?? '', value: toNumber(r[s.key]) })),
  }));
  // Scale by the largest magnitude, not the signed max — an all-negative series
  // (max signed value < 0) would otherwise scale against 0 and render empty.
  const max = series.reduce(
    (m, s) => s.points.reduce((mm, p) => Math.max(mm, Math.abs(p.value)), m),
    0,
  );
  const hasData = rows.length > 0 && series.some((s) => s.points.some((p) => p.value !== 0));
  return { labels, series, max, hasData };
}

/** A bar's height as a 0–100 percentage of the model max, by magnitude (0 when
 * max is 0). Negatives render at their absolute size; the signed value is carried
 * by the caller (tooltip + sr-only table). */
export function barHeightPct(value: number, max: number): number {
  if (max <= 0) return 0;
  return Math.min(100, (Math.abs(value) / max) * 100);
}
